import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { platformStorage } from '@/lib/platform-storage'
import { 
  TimerState, 
  TimerSyncStatus, 
  TimerConfig,
  DeviceInfo
} from '@/types/timer'
import { featureFlagService } from '@/lib/feature-flags'
import { createTimeEntry } from '@/lib/convex-time-entries'

/**
 * Sessions shorter than this are dropped rather than recorded.
 *
 * Every tracked interval feeds the duration-learning loop (deriveShapedInsight),
 * so a mis-tapped start/stop is not a harmless empty row: it drags the measured
 * factor for that shape toward zero.
 */
const MIN_TRACKED_SECONDS = 5

// Generate device and session IDs (SSR-safe).
const generateDeviceId = (): string => {
  if (typeof window === 'undefined') {
    return `device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  }
  
  try {
    const stored = localStorage.getItem('blitztask_device_id')
    if (stored) return stored
    
    const deviceId = `device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
    localStorage.setItem('blitztask_device_id', deviceId)
    return deviceId
  } catch {
    return `device_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
  }
}

const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

const generateTimerId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `timer_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
}

interface PersistentTimerStore {
  hasFetchedInitialTimer: boolean
  // State
  currentTimer: TimerState | null
  isLoading: boolean
  error: string | null
  syncStatus: TimerSyncStatus
  config: TimerConfig
  deviceInfo: DeviceInfo
  displayUpdateInterval: ReturnType<typeof setInterval> | null

  // Actions
  startTimer: (taskId: string, notes?: string) => Promise<boolean>
  pauseTimer: () => Promise<boolean>
  resumeTimer: () => Promise<boolean>
  stopTimer: (notes?: string, recordUsage?: boolean) => Promise<boolean>
  syncTimer: () => Promise<boolean>
  resetTimer: () => Promise<void>
  
  // Internal methods
  initTimerDisplay: () => Promise<void>
  processOfflineActions: () => Promise<void>
  updateTimerDisplay: () => void
  setError: (error: string | null) => void
  startDisplayUpdates: () => void
  stopDisplayUpdates: () => void
  // Legacy methods kept for compatibility
  startPeriodicSync: () => void
  stopPeriodicSync: () => void
}

const defaultConfig: TimerConfig = {
  syncInterval: 0,
  offlineQueueSize: 50,
  maxRetries: 3,
  retryDelay: 2000
}

const defaultSyncStatus: TimerSyncStatus = {
  isOnline: typeof window !== 'undefined' ? navigator.onLine : true,
  lastSyncTime: null,
  pendingActions: [],
  hasConflicts: false
}

export const usePersistentTimerStore = create<PersistentTimerStore>()(
  devtools(
    persist(
      (set, get) => ({
        hasFetchedInitialTimer: true,
        currentTimer: null,
        isLoading: false,
        error: null,
        syncStatus: defaultSyncStatus,
        config: defaultConfig,
        deviceInfo: {
          device_id: generateDeviceId(),
          client_session_id: generateSessionId(),
          user_agent: typeof window !== 'undefined' ? navigator.userAgent : 'Server',
          platform: typeof window !== 'undefined' ? navigator.platform : 'Server'
        },
        displayUpdateInterval: null,

        startTimer: async (taskId: string, notes?: string) => {
          set({ error: null })

          try {
            const { currentTimer } = get()
            if (currentTimer && currentTimer.status !== 'stopped') {
              await get().stopTimer('Stopped to start new timer', false)
            }

            const startTime = new Date().toISOString()
            const { deviceInfo } = get()

            const newTimer: TimerState = {
              id: generateTimerId(),
              user_id: '',
              task_id: taskId,
              start_time: startTime,
              server_start_time: startTime,
              last_sync_time: startTime,
              elapsed_time: 0,
              current_elapsed_time: 0,
              status: 'running',
              device_id: deviceInfo.device_id,
              client_session_id: deviceInfo.client_session_id,
              notes: notes || null,
              created_at: startTime,
              updated_at: startTime
            }

            set((state) => ({
              currentTimer: newTimer,
              isLoading: false,
              syncStatus: {
                ...state.syncStatus,
                lastSyncTime: startTime
              }
            }))
            get().startDisplayUpdates()
            return true
          } catch (error) {
            set({
              error:
                error instanceof Error
                  ? error.message
                  : 'Failed to start timer',
              isLoading: false,
              currentTimer: null
            })
            get().stopDisplayUpdates()
            return false
          }
        },

        pauseTimer: async () => {
          const { currentTimer } = get()
          if (!currentTimer || currentTimer.status !== 'running') return false

          set({ error: null })

          try {
            const now = new Date()
            const pauseTime = now.toISOString()
            
            const serverStartTime = new Date(currentTimer.server_start_time || currentTimer.start_time).getTime()
            const currentTime = now.getTime()
            const sessionDuration = Math.floor((currentTime - serverStartTime) / 1000)
            const finalElapsedTime = currentTimer.elapsed_time + sessionDuration

            const pausedTimer: TimerState = {
              ...currentTimer,
              status: 'paused',
              elapsed_time: finalElapsedTime,
              current_elapsed_time: finalElapsedTime,
              updated_at: pauseTime
            }

            set((state) => ({
              currentTimer: pausedTimer,
              isLoading: false,
              syncStatus: {
                ...state.syncStatus,
                lastSyncTime: pauseTime
              }
            }))
            get().stopDisplayUpdates()
            return true
          } catch (error) {
            console.error('Failed to pause timer:', error)
              set({ 
                error: error instanceof Error ? error.message : 'Failed to pause timer',
                isLoading: false
              })
              return false
          }
        },

        resumeTimer: async () => {
          const { currentTimer } = get()
          if (!currentTimer || currentTimer.status !== 'paused') return false

          set({ error: null })

          try {
            const now = new Date()
            const resumeTime = now.toISOString()
            
            const resumedTimer: TimerState = {
              ...currentTimer,
              status: 'running',
              server_start_time: resumeTime, // New session start time
              updated_at: resumeTime
            }

            set((state) => ({
              currentTimer: resumedTimer,
              isLoading: false,
              syncStatus: {
                ...state.syncStatus,
                lastSyncTime: resumeTime
              }
            }))
            get().startDisplayUpdates()
            return true
          } catch (error) {
            console.error('Failed to resume timer:', error)
              set({ 
                error: error instanceof Error ? error.message : 'Failed to resume timer',
                isLoading: false
              })
              return false
          }
        },

        stopTimer: async (notes?: string, recordUsage: boolean = true) => {
          const { currentTimer } = get()
          if (!currentTimer || currentTimer.status === 'stopped') return false

          set({ error: null })

          try {
            const now = new Date()
            const stopTime = now.toISOString()
            let finalElapsedTime = currentTimer.elapsed_time

            if (currentTimer.status === 'running') {
              const serverStartTime = new Date(currentTimer.server_start_time || currentTimer.start_time).getTime()
              const currentTime = now.getTime()
              const sessionDuration = Math.floor((currentTime - serverStartTime) / 1000)
              finalElapsedTime = currentTimer.elapsed_time + sessionDuration
            }

            set((state) => ({
              currentTimer: null,
              isLoading: false,
              syncStatus: {
                ...state.syncStatus,
                lastSyncTime: stopTime
              }
            }))
            get().stopDisplayUpdates()

            if (recordUsage) {
              try {
                await featureFlagService.recordFeatureUsage('timer_tasks', 'daily')
              } catch (usageError) {
                console.warn('Failed to record timer usage on completion:', usageError)
              }
            }

            // The interval is recorded after the state clear, not before: stopping
            // is the user's intent and the UI should reflect it immediately, so a
            // slow or failed write must never hold the timer open. `currentTimer`
            // is captured above, so clearing state does not lose the task id.
            //
            // `duration` is seconds and counts *tracked* time only — start_time and
            // stopTime bracket the wall clock, which is wider whenever the timer was
            // paused. This mirrors what the pomodoro path already writes.
            if (currentTimer.task_id && finalElapsedTime >= MIN_TRACKED_SECONDS) {
              try {
                await createTimeEntry({
                  task_id: currentTimer.task_id,
                  start_time: currentTimer.start_time,
                  end_time: stopTime,
                  duration: finalElapsedTime,
                  notes: notes ?? currentTimer.notes ?? null,
                })
              } catch (entryError) {
                console.error('Failed to record time entry on timer stop:', entryError)
              }
            }

            return true
          } catch (error) {
            console.error('Failed to stop timer:', error)
            set({ 
              error: error instanceof Error ? error.message : 'Failed to stop timer',
              isLoading: false 
            })
            return false
          }
        },

        syncTimer: async () => {
          const { currentTimer } = get()
          if (!currentTimer) return false

          try {
            if (currentTimer.status === 'running') {
              get().updateTimerDisplay()
            }
            return true
          } catch (error) {
            console.error('Timer sync failed:', error)
            return false
          }
        },

        initTimerDisplay: async () => {
          const timer = get().currentTimer
          if (timer?.status === 'running') {
            get().startDisplayUpdates()
            get().updateTimerDisplay()
          } else {
            get().stopDisplayUpdates()
          }
          set({ hasFetchedInitialTimer: true, isLoading: false, error: null })
        },

        processOfflineActions: async () => {
          set((state) => ({
            syncStatus: {
              ...state.syncStatus,
              pendingActions: [],
              hasConflicts: false
            }
          }))
        },

        updateTimerDisplay: () => {
          const { currentTimer } = get()
          if (!currentTimer || currentTimer.status !== 'running') return

          const serverStartTime = new Date(currentTimer.server_start_time || currentTimer.start_time).getTime()
          const currentTime = Date.now()
          const sessionDuration = Math.floor((currentTime - serverStartTime) / 1000)
          const currentElapsedTime = currentTimer.elapsed_time + sessionDuration

          if (Math.abs(currentElapsedTime - currentTimer.current_elapsed_time) >= 1) {
            set((state) => ({
              currentTimer: state.currentTimer ? {
                ...state.currentTimer,
                current_elapsed_time: currentElapsedTime
              } : null
            }))
          }
        },

        setError: (error: string | null) => {
          set({ error })
        },

        resetTimer: async () => {
          get().stopDisplayUpdates()
          set({
            currentTimer: null,
            error: null,
            isLoading: false,
            syncStatus: {
              ...get().syncStatus,
              lastSyncTime: new Date().toISOString(),
              pendingActions: [],
              hasConflicts: false
            }
          })
        },

        startDisplayUpdates: () => {
          if (get().displayUpdateInterval) return
          const interval = setInterval(() => {
            const { currentTimer } = get()
            if (currentTimer && currentTimer.status === 'running') {
              get().updateTimerDisplay()
            }
          }, 500)
          set({ displayUpdateInterval: interval })
        },

        stopDisplayUpdates: () => {
          const { displayUpdateInterval } = get()
          if (displayUpdateInterval) {
            clearInterval(displayUpdateInterval)
            set({ displayUpdateInterval: null })
          }
        },

        startPeriodicSync: () => {
          console.log('Periodic sync disabled - using client-driven timer')
        },

        stopPeriodicSync: () => {
          // No-op for compatibility.
        }
      }),
      {
        name: 'persistent-timer-store',
        // Named rather than defaulted: zustand falls back to `localStorage`,
        // which does not exist on a phone. Metro resolves the `.native`
        // sibling to AsyncStorage so this store file runs on both clients.
        storage: createJSONStorage(() => platformStorage),
        partialize: (state) => ({
          currentTimer: state.currentTimer,
          syncStatus: state.syncStatus,
          deviceInfo: state.deviceInfo,
          config: state.config
        }),
        onRehydrateStorage: () => {
          return (state, error) => {
            if (error) {
              console.error('Error rehydrating persistent timer store:', error)
              return
            }

            if (state?.currentTimer?.status === 'running') {
              setTimeout(() => {
                const store = usePersistentTimerStore.getState()
                if (store.currentTimer?.status === 'running' && !store.displayUpdateInterval) {
                  store.startDisplayUpdates()
                  store.updateTimerDisplay()
                }
              }, 100)
            }
          }
        }
      }
    ),
    { name: 'persistent-timer-store' }
  )
)

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    usePersistentTimerStore.setState((state) => ({
      syncStatus: { ...state.syncStatus, isOnline: true }
    }))
  })

  window.addEventListener('offline', () => {
    usePersistentTimerStore.setState((state) => ({
      syncStatus: { ...state.syncStatus, isOnline: false }
    }))
  })
}
